import {
  beautifulObjectDefaults,
  beautifulObjectFilters,
  beautifulObjectIds,
  SkyCoord,
  easing,
} from '@stellar-globe/stellar-globe'
import type { BeautifulObjectId, BeautifulObjectParams } from '@stellar-globe/stellar-globe'
import { LogScaleRange } from '@stellar-globe/react-stellar-globe'
import { resizableY } from '@stellar-globe/react-draggable-dialog'
import { memo } from 'react'
import { Icon } from '../../../common/components/Icon'
import { AppDialog } from '../../AppDialog'
import { useAppContext } from '../../context'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { prettyPicturesSlice } from './prettyPicturesSlice'
import styles from './styles.module.scss'
import { prettyPictureTitles, selectMixColumn } from './model'

export function PrettyPicturesDialogs() {
  return <>{beautifulObjectIds.map(which => <PrettyPictureDialog key={which} which={which} />)}</>
}

const PrettyPictureDialog = memo(({ which }: { which: BeautifulObjectId }) => {
  const dispatch = useAppDispatch()
  const { globeHandle } = useAppContext()
  const visible = useAppSelector(state => state.prettyPictures.dialogsOpen[which])
  const params = useAppSelector(state => state.prettyPictures.params[which])
  const filters = beautifulObjectFilters[which]

  const onChange = (next: BeautifulObjectParams) => {
    dispatch(prettyPicturesSlice.actions.paramsUpdated({ which, params: next }))
  }
  const setScalar = (key: 'exposure' | 'gamma' | 'ground', value: number) => {
    onChange({ ...params, [key]: value })
  }
  const setArray = (key: 'a' | 'b' | 'beta' | 'bias' | 'mix', index: number, value: number) => {
    const values = [...params[key]]
    values[index] = value
    onChange({ ...params, [key]: values })
  }

  const goThere = () => {
    const view = beautifulObjectDefaults[which].view
    const globe = globeHandle.current?.()
    if (!globe) return
    globe.camera.jumpTo(
      { fovy: view.fovy, roll: view.roll },
      {
        coord: SkyCoord.fromRad(view.a, view.d),
        easingFunction: easing.slowStartStop4,
        duration: 4000,
      },
    )
  }

  return (
    <AppDialog
      title={<><Icon type="tune" marginRight />{prettyPictureTitles[which]}</>}
      visible={visible}
      onCloseButtonClick={() => dispatch(prettyPicturesSlice.actions.dialogToggled({ which, open: false }))}
      resizable={resizableY}
      sizeHint={{ width: '510px' }}
      minmaxSize={{ maxHeight: '700px' }}
    >
      <div className={styles.panel}>
        <div className={styles.actions}>
          <button type="button" onClick={() => dispatch(prettyPicturesSlice.actions.paramsReset(which))}>Reset</button>
          <button type="button" onClick={goThere}>Go There</button>
        </div>
        <fieldset>
          <legend>RGB channel mixing</legend>
          <table className={styles.mixMatrix}>
            <thead>
              <tr>
                <th />
                {filters.map(filter => <th key={filter}>{filter}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>1</th>
                {filters.map((filter, column) => (
                  <td key={filter}>
                    <button
                      type="button"
                      title={'Use ' + filter + ' for all RGB channels'}
                      onClick={() => onChange({ ...params, mix: selectMixColumn(params.mix, column, filters.length) })}
                    >1</button>
                  </td>
                ))}
              </tr>
              {(['R', 'G', 'B'] as const).map((channel, row) => (
                <tr key={channel}>
                  <th>{channel}</th>
                  {filters.map((filter, column) => (
                    <td key={filter}>
                      <input
                        type="number"
                        aria-label={channel + ' from ' + filter}
                        step="0.01"
                        value={params.mix[column * 3 + row]}
                        onChange={event => {
                          const value = event.currentTarget.valueAsNumber
                          if (Number.isFinite(value)) {
                            setArray('mix', column * 3 + row, value)
                          }
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
        <table className={styles.paramsTable}>
          <tbody>
            <ParamRow name="E" title="Exposure" value={params.exposure} min={0} max={100}
              onChange={value => setScalar('exposure', value)} />
            <ParamRow name="γ" title="Gamma" value={params.gamma} min={0} max={10}
              onChange={value => setScalar('gamma', value)} />
            <ParamRow name="Θ" title="Background offset" value={params.ground} min={-1} max={1}
              onChange={value => setScalar('ground', value)} />
          </tbody>
        </table>
        {filters.map((filter, i) => (
          <fieldset key={filter}>
            <legend>{filter}</legend>
            <table className={styles.paramsTable}>
              <tbody>
                <ParamRow name="β" title="Asinh stretch" value={params.beta[i]} min={0} max={50000}
                  onChange={value => setArray('beta', i, value)} />
                <ParamRow name="a" title="Channel scale" value={params.a[i]} min={0} max={15} a={20}
                  onChange={value => setArray('a', i, value)} />
                <ParamRow name="b" title="Channel offset" value={params.b[i]} min={-2} max={2} a={10}
                  onChange={value => setArray('b', i, value)} />
                <ParamRow name="B" title="Channel bias" value={params.bias[i]} min={-1} max={1}
                  onChange={value => setArray('bias', i, value)} />
              </tbody>
            </table>
          </fieldset>
        ))}
      </div>
    </AppDialog>
  )
})

type ParamRowProps = {
  name: string
  title: string
  value: number
  min: number
  max: number
  a?: number
  onChange: (value: number) => void
}

function ParamRow({ name, title, value, min, max, a, onChange }: ParamRowProps) {
  return (
    <tr>
      <th title={title}>{name}</th>
      <td>
        <div className={styles.paramControl}>
          <LogScaleRange value={value} min={min} max={max} a={a} onInput={onChange} />
          <input
            type="number"
            aria-label={title}
            step="any"
            min={min}
            max={max}
            value={Number(value.toPrecision(6))}
            onChange={event => {
              const next = event.currentTarget.valueAsNumber
              if (Number.isFinite(next) && next >= min && next <= max) {
                onChange(next)
              }
            }}
          />
        </div>
      </td>
    </tr>
  )
}
